import {
  streamCbAiApi,
  fetchCbAiApi,
  EventResponse,
  type AiRoutingContext,
} from './cbAiApi';

export interface UserFeedback {
  relevanceScore?: number;
  sentiment?: boolean;
  comment?: string;
}

export interface AgentSession {
  id: string;
  agent_id: string;
  session_type: string;
  created_at: string;
  updated_at: string;
  messages: unknown[];
}

export interface CbAiApi {
  codeGenerationStream: (
    prompt: string,
    language: string,
    context: Record<string, string>,
    signal?: AbortSignal,
    sessionId?: string | null,
    routingContext?: AiRoutingContext
  ) => AsyncGenerator<EventResponse>;

  submitUserFeedback: (
    logId: string,
    feedback: UserFeedback,
    routingContext?: AiRoutingContext
  ) => Promise<boolean>;
}

export const cbAiApi: CbAiApi = {
  codeGenerationStream: async function* (
    prompt: string,
    language: string,
    context: Record<string, string>,
    signal?: AbortSignal,
    sessionId?: string | null,
    routingContext?: AiRoutingContext
  ) {
    try {
      let session: AgentSession;

      if (sessionId) {
        // Reuse existing session
        session = {
          id: sessionId,
          agent_id: '',
          session_type: 'copilot',
          created_at: '',
          updated_at: '',
          messages: [],
        };
      } else {
        // Create a new session
        session = await fetchCbAiApi<AgentSession>(
          '/api/public/api_explorer/agent_sessions',
          {
            method: 'POST',
          },
          'agent_session_create', // reCAPTCHA action
          routingContext
        );

        // Yield session ID in the first event for new sessions
        yield {
          eventType: 'session_created',
          payload: { sessionId: session.id },
        };
      }

      // Use the session-based messages endpoint
      const stream = streamCbAiApi<EventResponse>(
        `/api/public/api_explorer/agent_sessions/${session.id}/messages`,
        {
          method: 'POST',
          body: JSON.stringify({
            user_query: prompt,
            context: {
              language,
            },
          }),
          signal,
        },
        signal,
        'agent_message_send', // reCAPTCHA action
        routingContext
      );

      for await (const event of stream) {
        // Map new event types to existing ones for backward compatibility
        let mappedEvent = event;
        if (event.eventType === 'status-update') {
          mappedEvent = {
            eventType: 'status',
            payload: event.payload,
          };
        } else if (event.eventType === 'final-output') {
          mappedEvent = {
            eventType: 'done',
            payload: event.payload,
          };
        }
        yield mappedEvent;
      }
    } catch (error) {
      console.error('Error in codeGenerationStream:', error);

      if (
        error instanceof Error &&
        'code' in error &&
        (error.code === 'RECAPTCHA_VERIFICATION_FAILED' ||
          error.code === 'RECAPTCHA_TOKEN_MISSING')
      ) {
        yield {
          eventType: 'error',
          payload: {
            message:
              'Verification failed. Please refresh and try again. If issue persists, contact [support](https://support.chargebee.com/support/login)',
          },
        };
      }
    }
  },

  submitUserFeedback: (
    logId: string,
    feedback: UserFeedback,
    routingContext?: AiRoutingContext
  ) => {
    return fetchCbAiApi(
      `/api/public/logs/${logId}/feedback`,
      {
        method: 'PATCH',
        body: JSON.stringify(feedback),
      },
      undefined,
      routingContext
    )
      .then(() => true)
      .catch(() => false);
  },
};
